import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Wrench, HardHat, Home as HomeIcon, Zap, Droplets, Mountain, MapPin, Star, Send, Check, Phone, Clock, MessageCircle, Shield, Calendar } from "lucide-react";
import axios from "axios";
import ScrollReveal from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";
import StaticStats from "@/components/StaticStats";
import { WORK_TYPES, REVIEWS, CTA_DEVIS, GOOGLE_REVIEWS_URL, FACEBOOK_URL } from "@/data/forms";
import { PrivacyNote, Honeypot } from "@/components/FormExtras";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceCards = [
  { icon: HardHat, title: "Construction", img: "https://images.unsplash.com/photo-1710585761854-57ff6839395c?w=600&q=80&fm=webp" },
  { icon: Wrench, title: "Rénovation", img: "https://images.unsplash.com/photo-1764697757348-e3c87b076310?w=600&q=80&fm=webp" },
  { icon: HomeIcon, title: "Toiture", img: "https://images.pexels.com/photos/31771166/pexels-photo-31771166.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { icon: Droplets, title: "Plomberie", img: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { icon: Zap, title: "Électricité", img: "https://images.unsplash.com/photo-1660330590022-9f4ff56b63f6?w=600&q=80&fm=webp" },
  { icon: Mountain, title: "Terrassement", img: "https://images.unsplash.com/photo-1580901368813-93220b510345?w=600&q=80&fm=webp" },
];

const reviews = REVIEWS;

const LOCAL_BUSINESS = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  "@id": "https://vgiconstruct.be/#organization",
  "name": "VGI Construct",
  "legalName": "VGI Construct SRL",
  "url": "https://vgiconstruct.be/",
  "logo": "https://vgiconstruct.be/favicon-512.png",
  "image": "https://vgiconstruct.be/og-image.png",
  "description": "Entreprise générale de rénovation créée en 2018, active à Bruxelles et dans tout le Brabant. Devis gratuit – réponse sous 48 h.",
  "telephone": "+32466407988",
  "email": "info@vgiconstruct.be",
  "vatID": "BE0705849204",
  "foundingDate": "2018",
  "founder": { "@type": "Person", "name": "Daniel Gheorghe" },
  "priceRange": "€€",
  "address": { "@type": "PostalAddress", "streetAddress": "Bergensesteenweg 24/8", "addressLocality": "Sint-Pieters-Leeuw", "postalCode": "1600", "addressRegion": "Brabant flamand", "addressCountry": "BE" },
  "geo": { "@type": "GeoCoordinates", "latitude": 50.78, "longitude": 4.30 },
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "18:00" }
  ],
  "areaServed": [
    { "@type": "AdministrativeArea", "name": "Région de Bruxelles-Capitale" },
    { "@type": "AdministrativeArea", "name": "Brabant flamand" },
    { "@type": "AdministrativeArea", "name": "Brabant wallon" }
  ],
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "bestRating": "5", "reviewCount": "20" },
  "sameAs": [FACEBOOK_URL, GOOGLE_REVIEWS_URL]
};

/* ─── Typewriter hook ─── */
function useTypewriter(words, typingSpeed = 100, deleteSpeed = 60, pause = 2000) {
  const [text, setText] = useState("");
  const [wordIdx, setWordIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const word = words[wordIdx];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setText(word.substring(0, text.length + 1));
        if (text.length + 1 === word.length) setTimeout(() => setDeleting(true), pause);
      } else {
        setText(word.substring(0, text.length - 1));
        if (text.length === 0) { setDeleting(false); setWordIdx((wordIdx + 1) % words.length); }
      }
    }, deleting ? deleteSpeed : typingSpeed);
    return () => clearTimeout(timeout);
  }, [text, deleting, wordIdx, words, typingSpeed, deleteSpeed, pause]);
  return text;
}

/* ─── Floating particles canvas ─── */
function ParticlesBg() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const particles = [];
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    for (let i = 0; i < 60; i++) {
      particles.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, r: Math.random() * 1.5 + 0.3, dx: (Math.random() - 0.5) * 0.3, dy: (Math.random() - 0.5) * 0.3, o: Math.random() * 0.5 + 0.1 });
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139,0,0,${p.o})`; ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="hero-particles" style={{ width: '100%', height: '100%' }} />;
}

/* ─── Ripple on click ─── */
function handleRipple(e) {
  const btn = e.currentTarget;
  const circle = document.createElement("span");
  const rect = btn.getBoundingClientRect();
  circle.className = "ripple-circle";
  circle.style.left = `${e.clientX - rect.left - 10}px`;
  circle.style.top = `${e.clientY - rect.top - 10}px`;
  btn.appendChild(circle);
  setTimeout(() => circle.remove(), 600);
}

/* ─── Quote form on dark red ─── */
const emptyQuote = { first_name: "", last_name: "", phone: "", email: "", project_type: "", message: "", website: "" };
function QuoteFormDark() {
  const [form, setForm] = useState(emptyQuote);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const startedAt = useRef(Date.now());
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      await axios.post(`${API_URL}/quotes`, { full_name: `${form.first_name} ${form.last_name}`, phone: form.phone, email: form.email, project_type: form.project_type, message: form.message, website: form.website, elapsed_ms: Date.now() - startedAt.current });
      setSuccess(true); setForm(emptyQuote); startedAt.current = Date.now();
      setTimeout(() => setSuccess(false), 8000);
    } catch (err) { setError(err.response?.data?.detail || "Erreur"); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} data-testid="quote-form-simple" style={{ display: 'flex', flexDirection: 'column', gap: '14px', position: 'relative' }}>
      <Honeypot value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="max-sm:!grid-cols-1">
        <input type="text" placeholder="Prénom *" required className="input-dark" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
        <input type="text" placeholder="Nom *" required className="input-dark" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="max-sm:!grid-cols-1">
        <input type="tel" placeholder="Téléphone *" required className="input-dark" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
        <input type="email" placeholder="Email *" required className="input-dark" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <select required className="input-dark" value={form.project_type} onChange={e => setForm(f => ({ ...f, project_type: e.target.value }))}>
        <option value="">Type de travaux *</option>
        {WORK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      <textarea placeholder="Description courte du projet..." rows={3} className="input-dark" style={{ resize: 'none' }} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
      {error && <p style={{ color: '#fca5a5', fontSize: '14px' }}>{error}</p>}
      {success && <p style={{ color: '#86efac', fontSize: '14px', fontWeight: 700 }}>Demande envoyée ! Un e-mail de confirmation vous a été adressé. Réponse sous 48 h.</p>}
      <button type="submit" disabled={loading} style={{ width: '100%', backgroundColor: '#fff', color: '#8B0000', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '14px', padding: '16px', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loading ? 0.5 : 1 }}>
        {loading ? "Envoi..." : <><Send size={16} /> Envoyer ma demande <ArrowRight size={16} /></>}
      </button>
      <PrivacyNote dark />
    </form>
  );
}

export default function Home() {
  const typewriterWord = useTypewriter(["Construction", "Rénovation", "Terrassement", "Toiture", "Plomberie"], 120, 70, 1800);
  const photoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (photoRef.current) photoRef.current.style.transform = `translateY(${window.scrollY * 0.08}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div data-testid="home-page" style={{ backgroundColor: '#fff' }}>
      <SEOHead title="VGI Construct | Entreprise générale de rénovation – Bruxelles & Brabant – Devis gratuit" description="VGI Construct SRL, entreprise générale de rénovation créée en 2018, active à Bruxelles et dans tout le Brabant. Rénovation complète, extension, toiture, électricité, plomberie. Devis gratuit – réponse sous 48 h." path="/">
        <script type="application/ld+json">{JSON.stringify(LOCAL_BUSINESS)}</script>
      </SEOHead>

      {/* ═══════════════ HERO PREMIUM + PARTICLES + TYPEWRITER ═══════════════ */}
      <section data-testid="hero-section" style={{ backgroundColor: '#0a0a0a', position: 'relative', overflow: 'hidden' }}>
        {/* Grain texture */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '128px', zIndex: 0 }} />
        <ParticlesBg />
        <span className="deco-number">01</span>

        <div style={{ maxWidth: '1360px', margin: '0 auto', padding: '60px 32px 80px', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px', alignItems: 'center' }} className="lg:!grid-cols-[55%_45%]">

            {/* LEFT — Text */}
            <div>
              <div className="animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards', opacity: 0, marginBottom: '28px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px', padding: '8px 20px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)' }}>
                  <Star size={14} color="#8B0000" fill="#8B0000" /> Entreprise générale de rénovation depuis 2018
                </span>
              </div>

              {/* Title with typewriter + animated line */}
              <div className="animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards', opacity: 0, display: 'flex', gap: '20px', marginBottom: '12px' }}>
                <div className="animated-line" style={{ width: '4px', backgroundColor: '#8B0000', borderRadius: '2px', flexShrink: 0 }} />
                <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 900, fontSize: 'clamp(2.8rem, 5.5vw, 4.5rem)', lineHeight: 1.05, letterSpacing: '-0.03em', margin: 0 }}>
                  <span style={{ display: 'block', color: '#ffffff' }}>Expert en</span>
                  <span className="glitch-text" data-text={typewriterWord} style={{ display: 'block', color: '#8B0000', minHeight: '1.1em' }}>
                    {typewriterWord}<span className="typewriter-cursor" />
                  </span>
                  <span style={{ display: 'block', color: '#ffffff' }}>à Bruxelles</span>
                </h1>
              </div>

              {/* Animated underline */}
              <div className="animate-fade-in-up" style={{ animationDelay: '0.35s', animationFillMode: 'forwards', opacity: 0, marginBottom: '20px', marginLeft: '24px' }}>
                <div style={{ height: '2px', background: 'linear-gradient(90deg, #8B0000, transparent)', width: '120px' }} />
              </div>

              <p className="animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards', opacity: 0, color: 'rgba(255,255,255,0.6)', fontSize: '17px', marginBottom: '12px', letterSpacing: '0.01em' }}>
                Travail soigné – Chantier propre – Délais respectés
              </p>

              <div className="animate-fade-in-up" style={{ animationDelay: '0.5s', animationFillMode: 'forwards', opacity: 0, display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.4)', fontSize: '14px', marginBottom: '32px' }}>
                <MapPin size={15} color="#8B0000" /> Active à Bruxelles et dans tout le Brabant
              </div>

              {/* Buttons with shimmer + ripple */}
              <div className="animate-fade-in-up" style={{ animationDelay: '0.6s', animationFillMode: 'forwards', opacity: 0, display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <Link to="/demande-devis" data-testid="hero-cta-devis" className="btn-shimmer btn-ripple" onClick={handleRipple}
                  style={{ backgroundColor: '#8B0000', color: '#fff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '13px', padding: '14px 28px', textDecoration: 'none', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  {CTA_DEVIS} <ArrowRight size={16} />
                </Link>
                <a href="https://wa.me/32466407988" target="_blank" rel="noopener noreferrer" className="btn-shimmer btn-ripple" onClick={handleRipple}
                  style={{ backgroundColor: '#25D366', color: '#fff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '13px', padding: '14px 24px', textDecoration: 'none', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <MessageCircle size={16} /> WhatsApp
                </a>
                <a href="tel:+32466407988" className="btn-shimmer btn-ripple" onClick={handleRipple}
                  style={{ border: '2px solid rgba(255,255,255,0.25)', color: '#fff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '13px', padding: '12px 24px', textDecoration: 'none', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent' }}>
                  <Phone size={16} /> +32 466 407 988
                </a>
              </div>
            </div>

            {/* RIGHT — Photo with parallax + pulsing border */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards', opacity: 0 }}>
              <div style={{ position: 'relative' }}>
                <div style={{ overflow: 'hidden', borderRadius: '20px' }}>
                  <img ref={photoRef} src="https://images.unsplash.com/photo-1697289934707-c139aa38f94c?w=700&q=85&fm=webp" alt="Ouvrier sur chantier de construction" fetchPriority="high" width="700" height="933"
                    className="photo-premium" style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', borderRadius: '20px', display: 'block', willChange: 'transform' }} />
                </div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', borderRadius: '0 0 20px 20px', background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)', pointerEvents: 'none' }} />
                <div className="badge-float" style={{ position: 'absolute', bottom: '24px', left: '24px', backgroundColor: 'rgba(139,0,0,0.85)', borderRadius: '12px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px', backdropFilter: 'blur(12px)', border: '1px solid rgba(139,0,0,0.3)' }}>
                  <Calendar size={22} color="#fff" />
                  <div>
                    <span style={{ color: '#fff', fontWeight: 900, fontSize: '18px', fontFamily: "'Cabinet Grotesk', sans-serif", display: 'block', lineHeight: 1.2 }}>Depuis 2018</span>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>67 chantiers livrés</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chiffres clés (statiques, présents dans le HTML) */}
        <div style={{ backgroundColor: '#1a1a1a', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ maxWidth: '1360px', margin: '0 auto', padding: '24px 32px' }}>
            <StaticStats />
          </div>
        </div>

        {/* Wave divider */}
        <div style={{ position: 'absolute', bottom: -1, left: 0, width: '100%', overflow: 'hidden', lineHeight: 0, zIndex: 10 }}>
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: '60px', display: 'block' }}>
            <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,30 1440,40 L1440,80 L0,80 Z" fill="#ffffff" />
            <path d="M0,50 C240,70 480,20 720,50 C960,80 1200,30 1440,50 L1440,80 L0,80 Z" fill="#ffffff" opacity="0.5" />
          </svg>
        </div>
      </section>

      {/* ═══════════════ POURQUOI NOUS CHOISIR ═══════════════ */}
      <section data-testid="why-us-section" style={{ backgroundColor: '#ffffff', padding: '48px 0 32px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: '36px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
                <div style={{ width: '40px', height: '2px', backgroundColor: '#8B0000' }} />
                <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', margin: 0 }}>Nos engagements</p>
                <div style={{ width: '40px', height: '2px', backgroundColor: '#8B0000' }} />
              </div>
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, color: '#1a1a1a' }}>Pourquoi VGI Construct ?</h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }} className="max-lg:!grid-cols-2 max-sm:!grid-cols-1">
            {[
              { icon: Shield, title: "Garantie Décennale", desc: "Tous nos travaux sont couverts par notre garantie décennale, conformément à la législation belge.", color: "#8B0000" },
              { icon: Clock, title: "Devis gratuit – réponse sous 48 h", desc: "Visite gratuite sur place et devis détaillé personnalisé sous 48 h.", color: "#1a6b00" },
              { icon: Calendar, title: "Depuis 2018", desc: "Société créée en 2018 par Daniel Gheorghe, 10 ans d'expérience dans le bâtiment. Active à Bruxelles et dans tout le Brabant.", color: "#B8860B" },
              { icon: Check, title: "Conformité RGIE", desc: "Mises en conformité électrique avec réception par organisme agréé VINÇOTTE.", color: "#2563eb" },
            ].map((card, i) => (
              <ScrollReveal key={card.title} delay={i * 100}>
                <div data-testid={`why-card-${i}`} style={{ backgroundColor: '#fafafa', border: '1px solid #e8e8e8', borderRadius: '12px', padding: '28px 24px', height: '100%', transition: 'all 0.3s' }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = '#8B0000'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(139,0,0,0.08)'; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <card.icon size={24} color="#fff" />
                  </div>
                  <h3 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '17px', fontWeight: 800, color: '#1a1a1a', marginBottom: '8px' }}>{card.title}</h3>
                  <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6, margin: 0 }}>{card.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SERVICES ═══════════════ */}
      <section data-testid="services-section" style={{ backgroundColor: '#ffffff', padding: '32px 0 48px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <ScrollReveal>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <div style={{ width: '4px', height: '40px', backgroundColor: '#8B0000', borderRadius: '2px' }} />
              <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', margin: 0 }}>Nos services</p>
            </div>
            <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 900, color: '#1a1a1a', marginBottom: '16px' }}>Nous faisons de tout !</h2>
            <p style={{ color: '#666', fontSize: '17px', marginBottom: '32px', maxWidth: '600px' }}>Construction, rénovation, toiture, plomberie, électricité et terrassement</p>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }} className="max-lg:!grid-cols-2 max-sm:!grid-cols-1" data-testid="services-grid">
            {serviceCards.map((svc, i) => (
              <ScrollReveal key={svc.title} delay={i * 80}>
                <div data-testid={`service-card-${i}`} className="group" style={{ position: 'relative', height: '220px', overflow: 'hidden', borderRadius: '8px', cursor: 'pointer' }}>
                  <img src={svc.img} alt={svc.title} loading="lazy" width="600" height="400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }} className="group-hover:scale-110" />
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,26,26,0.4)', transition: 'background 0.5s' }} className="group-hover:!bg-[rgba(26,26,26,0.55)]" />
                  <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '16px' }}>
                    <svc.icon size={40} color="#fff" style={{ marginBottom: '12px', transition: 'transform 0.3s' }} className="group-hover:scale-110" />
                    <h3 style={{ color: '#fff', fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '20px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{svc.title}</h3>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={500}>
            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <Link to="/services" data-testid="services-view-all" className="btn-primary" style={{ fontSize: '13px' }}>Voir tous nos services <ArrowRight size={16} /></Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══════════════ AVIS ═══════════════ */}
      <section data-testid="reviews-section" style={{ backgroundColor: '#ffffff', padding: '56px 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', marginBottom: '16px' }}>Témoignages</p>
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 900, color: '#1a1a1a', marginBottom: '16px' }}>Ce que disent nos clients</h2>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span data-testid="google-rating" style={{ color: '#1a1a1a', fontSize: '17px', fontWeight: 800 }}>5,0/5 sur Google</span>
                <div style={{ display: 'flex' }}>{[...Array(5)].map((_, i) => <Star key={i} size={18} color="#8B0000" fill="#8B0000" />)}</div>
                <span style={{ color: '#666', fontSize: '17px' }}>– plus de 20 avis</span>
              </div>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {reviews.map((r, i) => (
              <ScrollReveal key={r.name} delay={i * 100}>
                <div data-testid={`review-card-${i}`} style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '28px', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 16px rgba(0,0,0,0.04)', transition: 'box-shadow 0.3s' }}>
                  <div style={{ display: 'flex', marginBottom: '16px' }}>{[...Array(5)].map((_, j) => <Star key={j} size={16} color="#8B0000" fill="#8B0000" />)}</div>
                  <p style={{ color: '#444', fontSize: '15px', lineHeight: 1.7, flex: 1, marginBottom: '20px' }}>"{r.text}"</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, #8B0000, #c44)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: '#fff', fontWeight: 900, fontSize: '16px' }}>{r.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p style={{ color: '#1a1a1a', fontWeight: 700, fontSize: '14px', margin: 0 }}>{r.name}</p>
                      <p style={{ color: '#999', fontSize: '12px', margin: 0 }}>Avis Google</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={600}>
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link" className="btn-secondary" style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Voir tous nos avis Google <ArrowRight size={16} />
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══════════════ PARTENAIRES ═══════════════ */}
      <section data-testid="partners-section" style={{ backgroundColor: '#0a0a0a', padding: '56px 0', overflow: 'hidden', position: 'relative' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px', marginBottom: '40px' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '2px', backgroundColor: '#8B0000' }} />
                <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', margin: 0 }}>Partenaires</p>
                <div style={{ width: '40px', height: '2px', backgroundColor: '#8B0000' }} />
              </div>
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, color: '#fff' }}>Ils nous font confiance</h2>
            </div>
          </ScrollReveal>
        </div>
        {/* Infinite scrolling carousel */}
        <div className="partners-carousel" style={{ display: 'flex', width: 'max-content' }}>
          {[...Array(3)].map((_, setIdx) => (
            <div key={setIdx} className="partners-track" style={{ display: 'flex', alignItems: 'center', gap: '60px', paddingRight: '60px' }}>
              {[
                { name: "Atelier Des 2 Maisons", logo: "/partners/a2maisons.webp", url: "https://www.a2maisons.be", invertOnDark: false, lightBg: true },
                { name: "Tecnoflex", logo: "/partners/tecnoflex.webp", url: "https://www.tecnoflex.be", invertOnDark: false, lightBg: true },
                { name: "Djoser", logo: "/partners/djoser.webp", url: "https://djoser-mat.be", invertOnDark: false, lightBg: true },
                { name: "Apok", logo: null, url: "https://www.apok.be/fr-be", isSvg: true },
              ].map((p, i) => (
                <a key={`${setIdx}-${i}`} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`partner-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                  className="partner-logo-link"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '200px', height: '80px', backgroundColor: p.lightBg ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.05)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', padding: '16px 24px', flexShrink: 0, transition: 'all 0.4s', textDecoration: 'none' }}>
                  {p.isSvg ? (
                    <svg viewBox="0 0 442 125" style={{ height: '40px', width: 'auto', opacity: 0.5, filter: 'grayscale(100%)', transition: 'all 0.4s' }} className="partner-svg">
                      <g fill="none"><path d="M325.678 62.915c0 29.224-23.585 52.916-52.677 52.916-29.093 0-52.677-23.692-52.677-52.916 0-29.224 23.584-52.915 52.677-52.915 29.092 0 52.677 23.691 52.677 52.915" fill="#FF8E00"/><path d="M42.032 74.858l18.342-41.453 17.542 41.453H42.032zm51.966 38h26.747L74.51 13.061H46.999L0 112.857H25.22l10.02-22.645h49.173l9.586 22.645zm87.333-63.126c-3.153 7.348-11.358 8.924-18.486 9.462-3.823.285-7.69.305-11.532.1V28.888c6.786-.238 18.234-.148 23.435 2.725 6.28 3.468 9.5 11.32 6.583 18.118m22.5-20.764c-3.031-4.787-7.909-7.663-12.653-10.532-11.208-6.78-43.813-5.375-43.813-5.375h-19.743v99.797h23.691V75.171c4.106.24 21.646.913 35.828-3.8 10.57-3.283 18.6-11.907 20.213-22.996.985-6.767.192-13.545-3.523-19.407m203.998 84.274h32.098l-51.594-52.779 49.035-47.522h-29.614l-43.23 43.426V12.94h-24.2v100.3h24.2V83.536l8.617-8.35z" fill="#FFF"/></g>
                    </svg>
                  ) : (
                    <img src={p.logo} alt={p.name} loading="lazy" width="160" height="45" style={{ maxHeight: '45px', maxWidth: '160px', objectFit: 'contain', filter: p.invertOnDark ? 'grayscale(100%) brightness(2) invert(0)' : 'grayscale(100%) brightness(0.7)', opacity: p.invertOnDark ? 0.8 : 0.5, transition: 'all 0.4s' }} className="partner-img" />
                  )}
                </a>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════ FORMULAIRE ═══════════════ */}
      <section data-testid="quote-section" style={{ backgroundColor: '#8B0000', padding: '56px 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '64px', alignItems: 'center' }} className="lg:!grid-cols-2">
            <ScrollReveal>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.25em', marginBottom: '16px' }}>Devis gratuit</p>
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 900, color: '#fff', marginBottom: '24px', lineHeight: 1.1 }}>Obtenez votre<br/>devis gratuit</h2>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '18px', marginBottom: '40px' }}>Devis gratuit – réponse sous 48 h</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {["Gratuit et sans engagement", "Réponse sous 48 h", "Active à Bruxelles et dans tout le Brabant"].map(t => (
                  <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '28px', height: '28px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Check size={14} color="#fff" /></div>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>{t}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}><QuoteFormDark /></ScrollReveal>
          </div>
        </div>
      </section>

      {/* ═══════════════ ZONES ═══════════════ */}
      <section data-testid="zones-section" style={{ backgroundColor: '#ffffff', padding: '56px 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', marginBottom: '16px' }}>Zone d'intervention</p>
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, color: '#1a1a1a' }}>Intervention rapide dans toute la zone</h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {[
              { name: "Bruxelles", desc: "19 communes" },
              { name: "Brabant flamand", desc: "Hal, Vilvorde, Zaventem..." },
              { name: "Brabant wallon", desc: "Waterloo, Wavre, Nivelles..." },
            ].map((z, i) => (
              <ScrollReveal key={z.name} delay={i * 120}>
                <Link to="/zones-intervention" className="group" style={{ display: 'block', backgroundColor: '#ffffff', borderRadius: '12px', padding: '36px', textAlign: 'center', textDecoration: 'none', transition: 'all 0.4s', border: '1px solid #e5e5e5' }}>
                  <MapPin size={36} color="#8B0000" style={{ margin: '0 auto 16px', transition: 'transform 0.3s' }} className="group-hover:scale-110" />
                  <h3 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", color: '#1a1a1a', fontSize: '22px', fontWeight: 900, marginBottom: '8px' }}>{z.name}</h3>
                  <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>{z.desc}</p>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={400}>
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <Link to="/zones-intervention" data-testid="zones-view-all" className="btn-secondary" style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Voir toutes nos zones <ArrowRight size={16} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
